import { z } from "zod/v4";
import {
  SectionSchema,
  SubsectionSchema,
  ViewableFieldSchema,
  BlockConfigSchema as BlockConfigSchemaCore,
} from "skedyul-core/features/crm/features/pages";
import { resolveCalendarTimeWindow } from "../blocks/calendar/timeWindow";
import {
  readPersistedCalendarFieldMap,
  readPersistedCalendarVisibility,
} from "../blocks/calendar/calendarConfigRead";

export type ViewableField = z.infer<typeof ViewableFieldSchema>;
export type KanbanSection = z.infer<typeof SectionSchema>;
export type KanbanSubSection = z.infer<typeof SubsectionSchema>;

export type BlockConfig = z.infer<typeof BlockConfigSchemaCore>;
export const BlockConfigSchema =
  BlockConfigSchemaCore as z.ZodType<BlockConfig>;

export interface BlockConfigContainer {
  type?: "kanban" | "spreadsheet" | "form" | "link" | "calendar";
  config?: {
    spreadsheet?: {
      columns?: Array<{ field: string; width?: number; model?: string }>;
    } | null;
    kanban?: {
      columns?: Array<{ field: string; width?: number; model?: string }>;
      statusBadgeField?: string;
      descriptionField?: string;
    } | null;
    calendar?: {
      event?: {
        start?: string;
        end?: string | null;
        recurrence?: string | null;
        seriesId?: string | null;
        originalStart?: string | null;
        calendar?: string | null;
      };
      calendarFieldMap?: Record<string, string>;
      visibleCalendarIds?: string[];
      defaultCalendarId?: string;
      mode?: "day" | "week" | "month";
      weekdays?: Array<"sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat">;
      timeWindow?: {
        start?: string;
        end?: string;
        stepMinutes?: number;
      };
      sync?: {
        enabled?: boolean;
        calendarIds?: string[];
        direction?: "push" | "pull" | "both";
        externalReadOnly?: boolean;
      };
    } | null;
    form?: unknown;
    link?: unknown;
  } | null;
  sort?: unknown;
  fields?: ViewableField[];
  section?: KanbanSection;
  subsection?: KanbanSubSection;
}

const BlockConfigSchemaTyped =
  BlockConfigSchema as unknown as z.ZodType<BlockConfigContainer>;

const BLOCK_VIEW_TYPES = new Set<BlockConfigContainer["type"]>([
  "kanban",
  "spreadsheet",
  "form",
  "link",
  "calendar",
]);

const coerceViewableFields = (raw: unknown): ViewableField[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  const fields: ViewableField[] = [];
  for (const entry of raw) {
    const parsed = ViewableFieldSchema.safeParse(entry);
    if (parsed.success) {
      fields.push(parsed.data);
    }
  }
  return fields;
};

export const coerceBlockConfigContainer = (
  input: unknown,
): BlockConfigContainer | undefined => {
  if (!input || typeof input !== "object") {
    return undefined;
  }

  const raw = input as Record<string, unknown>;
  const rawType = raw.type;
  const type = BLOCK_VIEW_TYPES.has(rawType as BlockConfigContainer["type"])
    ? (rawType as BlockConfigContainer["type"])
    : "spreadsheet";

  const rawConfig =
    raw.config && typeof raw.config === "object"
      ? (raw.config as BlockConfigContainer["config"])
      : {};

  const parsedSection = SectionSchema.safeParse(raw.section);
  const parsedSubsection = SubsectionSchema.safeParse(raw.subsection);

  return {
    type,
    config: rawConfig ?? {},
    sort: raw.sort,
    fields: coerceViewableFields(raw.fields),
    ...(parsedSection.success ? { section: parsedSection.data } : {}),
    ...(parsedSubsection.success ? { subsection: parsedSubsection.data } : {}),
  };
};

export const parseBlockConfig = (
  input: unknown,
): BlockConfigContainer | undefined => {
  if (!input || typeof input !== "object") return undefined;
  const parsed = BlockConfigSchemaTyped.safeParse(input);
  if (parsed.success) return parsed.data;
  return coerceBlockConfigContainer(input);
};

export type ViewType = "spreadsheet" | "kanban" | "calendar";

export const getActiveViewType = (config?: BlockConfigContainer): ViewType => {
  if (!config) return "spreadsheet";
  if (config.type === "kanban") return "kanban";
  if (config.type === "calendar") return "calendar";
  return "spreadsheet";
};

export type SpreadsheetColumnLike = {
  field: string;
  width?: number;
  model?: string;
};

export function normalizeSpreadsheetColumnWidth(
  width: unknown,
): number | undefined {
  if (typeof width === "number" && Number.isFinite(width)) {
    return width;
  }
  if (typeof width === "string" && width.trim() !== "") {
    const parsed = Number(width);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

export function toPersistedSpreadsheetColumns(
  columns: Array<{
    field: string;
    width?: unknown;
    model?: string;
  }>,
): SpreadsheetColumnLike[] {
  return columns.map((col) => {
    const persisted: SpreadsheetColumnLike = { field: col.field };
    if (col.model) {
      persisted.model = col.model;
    }
    const width = normalizeSpreadsheetColumnWidth(col.width);
    if (width !== undefined) {
      persisted.width = width;
    }
    return persisted;
  });
}

export type SpreadsheetConfigLike = {
  columns: SpreadsheetColumnLike[];
};

export type LocalCalendarConfig = {
  type: "calendar";
  startField: string;
  endField?: string;
  recurrenceField?: string;
  seriesIdField?: string;
  originalStartField?: string;
  calendarField?: string;
  timezoneField?: string;
  calendarFieldMap?: Record<string, string>;
  visibleCalendarIds?: string[];
  defaultCalendarId?: string;
  includes: string[];
  mode?: "day" | "week" | "month";
  startTime?: string;
  endTime?: string;
  stepMinutes?: number;
  weekdays?: Array<"sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat">;
  sync?: {
    enabled?: boolean;
    calendarIds?: string[];
    direction?: "push" | "pull" | "both";
    externalReadOnly?: boolean;
  };
};

export type LocalKanbanConfig = {
  type: "kanban";
  statusBadgeField?: string;
  descriptionField?: string;
};

export type CoercedSpreadsheetConfig = {
  type: "spreadsheet";
  columns: SpreadsheetColumnLike[];
};

export type CoercedKanbanConfig = {
  type: "kanban";
  columns?: { field: string; model?: string; width?: number }[];
  section?: KanbanSection;
  subsection?: KanbanSubSection;
};

export type CoercedCalendarConfig = LocalCalendarConfig;

export type CoercedConfig =
  | CoercedSpreadsheetConfig
  | CoercedKanbanConfig
  | CoercedCalendarConfig;

export const getSpreadsheetConfigFromContainer = (
  config?: BlockConfigContainer,
): SpreadsheetConfigLike | undefined => {
  if (!config) return undefined;
  const raw = config.config?.spreadsheet as
    | {
        columns?: Array<{
          field: string;
          width?: number;
          model?: string;
        }>;
      }
    | null
    | undefined;
  if (!raw || !Array.isArray(raw.columns)) {
    return { columns: [] };
  }
  return {
    columns: raw.columns,
  };
};

export const getLocalCalendarFromContainer = (
  config?: BlockConfigContainer,
): LocalCalendarConfig | undefined => {
  if (!config || config.type !== "calendar") return undefined;
  const calendar = config.config?.calendar;
  if (!calendar || typeof calendar !== "object") {
    return { type: "calendar", startField: "", includes: [] };
  }

  const event =
    "event" in calendar && calendar.event
      ? (calendar.event as {
          start?: unknown;
          end?: unknown;
          recurrence?: unknown;
          seriesId?: unknown;
          originalStart?: unknown;
          calendar?: unknown;
        })
      : {};

  const startField = typeof event.start === "string" ? event.start : "";
  const endField =
    typeof event.end === "string"
      ? (event.end as string | null) ?? undefined
      : undefined;
  const recurrenceField =
    typeof event.recurrence === "string" && event.recurrence.length > 0
      ? event.recurrence
      : undefined;
  const seriesIdField =
    typeof event.seriesId === "string" && event.seriesId.length > 0
      ? event.seriesId
      : undefined;
  const originalStartField =
    typeof event.originalStart === "string" && event.originalStart.length > 0
      ? event.originalStart
      : undefined;
  const persistedVisibility = readPersistedCalendarVisibility(config);
  const calendarField = persistedVisibility.calendarHandle;
  const calendarFieldMap = readPersistedCalendarFieldMap(config);
  const visibleCalendarIds = persistedVisibility.visibleCalendarIds;
  const defaultCalendarId = persistedVisibility.defaultCalendarId;

  const modeRaw = (calendar as { mode?: unknown }).mode;
  const mode =
    modeRaw === "day" || modeRaw === "week" || modeRaw === "month"
      ? modeRaw
      : undefined;

  const weekdays = Array.isArray((calendar as { weekdays?: unknown }).weekdays)
    ? (
        calendar as {
          weekdays?: LocalCalendarConfig["weekdays"];
        }
      ).weekdays
    : undefined;

  const timeWindow = (
    calendar as {
      timeWindow?: { start?: string; end?: string; stepMinutes?: number };
    }
  ).timeWindow;

  const syncRaw = (calendar as { sync?: LocalCalendarConfig["sync"] }).sync;

  const { start: resolvedStartTime, end: resolvedEndTime } =
    resolveCalendarTimeWindow(timeWindow?.start, timeWindow?.end);

  return {
    type: "calendar",
    startField,
    ...(endField ? { endField } : {}),
    ...(recurrenceField ? { recurrenceField } : {}),
    ...(seriesIdField ? { seriesIdField } : {}),
    ...(originalStartField ? { originalStartField } : {}),
    ...(calendarField ? { calendarField } : {}),
    ...(calendarFieldMap && Object.keys(calendarFieldMap).length > 0
      ? { calendarFieldMap }
      : {}),
    ...(visibleCalendarIds ? { visibleCalendarIds } : {}),
    ...(defaultCalendarId ? { defaultCalendarId } : {}),
    ...(mode ? { mode } : {}),
    includes: [],
    ...(weekdays ? { weekdays } : {}),
    startTime: resolvedStartTime,
    endTime: resolvedEndTime,
    ...(typeof timeWindow?.stepMinutes === "number"
      ? { stepMinutes: timeWindow.stepMinutes }
      : {}),
    ...(syncRaw ? { sync: syncRaw } : {}),
  };
};

export const getLocalKanbanFromContainer = (
  config?: BlockConfigContainer,
): LocalKanbanConfig | undefined => {
  if (!config || config.type !== "kanban") return undefined;
  const kanban = config.config?.kanban;
  if (!kanban || typeof kanban !== "object") {
    return { type: "kanban" };
  }

  const statusBadgeField =
    typeof kanban.statusBadgeField === "string" && kanban.statusBadgeField
      ? kanban.statusBadgeField
      : undefined;
  const descriptionField =
    typeof kanban.descriptionField === "string" && kanban.descriptionField
      ? kanban.descriptionField
      : undefined;

  return {
    type: "kanban",
    ...(statusBadgeField ? { statusBadgeField } : {}),
    ...(descriptionField ? { descriptionField } : {}),
  };
};

export const coerceBlockConfig = (
  input: unknown,
): CoercedConfig | undefined => {
  const container = parseBlockConfig(input);
  if (!container) return undefined;
  const view = getActiveViewType(container);

  if (view === "spreadsheet") {
    const spreadsheet = getSpreadsheetConfigFromContainer(container) ?? {
      columns: [],
    };
    const result: CoercedSpreadsheetConfig = {
      type: "spreadsheet",
      columns: spreadsheet.columns,
    };
    return result;
  }

  if (view === "kanban") {
    const columnsFromFields =
      Array.isArray(container.fields) && container.fields.length > 0
        ? container.fields.map((vf) => {
            const modelHandle =
              vf &&
              typeof vf === "object" &&
              "model" in vf &&
              vf.model &&
              typeof (vf.model as { handle?: unknown }).handle === "string"
                ? String((vf.model as { handle?: string }).handle)
                : "";
            const fieldHandle =
              vf &&
              typeof vf === "object" &&
              "handle" in vf &&
              typeof (vf as { handle?: unknown }).handle === "string"
                ? String((vf as { handle?: string }).handle)
                : "";
            return {
              field: fieldHandle,
              model: modelHandle || undefined,
            };
          })
        : undefined;

    const result: CoercedKanbanConfig = {
      type: "kanban",
      columns: columnsFromFields,
      section: container.section,
      subsection: container.subsection,
    };
    return result;
  }

  if (view === "calendar") {
    const calendar = getLocalCalendarFromContainer(container) ?? {
      type: "calendar",
      startField: "",
      includes: [],
    };
    const result: CoercedCalendarConfig = calendar;
    return result;
  }

  return undefined;
};
